import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { selectBucketNoData } from "../../app/bucketsSlice/bucketSelectors";
import { selectNoData as SelectNoSingleRequestData } from "../../app/certificateRequestSlice";
import {
    selectIsReviewComplete,
    selectNoData as selectNoCertificateData,
} from "../../app/certificateSlice";
import {
    selectLoading as selectCustomerLoading,
    selectNoData as selectNoCustomerData,
} from "../../app/customerSlice";
import { selectNoData as selectNoJobData } from "../../app/jobsSlice";
import { selectNoData as selectNoBulkRequestData } from "../../app/requestSlice";
import { selectNoData as selectNoSavedSearchData } from "../../app/savedSearchesSlice";
import featureFlag from "../../featureToggler/featureFlag";
import { useIsAuthorized } from "../../hooks/useIsAuthorized";
import { REQUEST_TYPES } from "../../shared/constants";
import {
    assignToGlobalImports,
    getDefaultReturnUrl,
    shouldUseGlobalImportShell,
} from "../../shared/globalImports";
import { getAssetURL } from "../../shared/Utils";
import "./no-data.css";

export const noDataMessage = "No records found.";
export const noDataDescription = "Adjust your search and try again";

const NoData = React.memo(props => {
    const navigate = useNavigate();
    const isGlobalImportsShellEnabled = useIsAuthorized(featureFlag.imports.globalImports);
    const goToCustomerImport = useCallback(
        e => {
            e.preventDefault();
            if (shouldUseGlobalImportShell(isGlobalImportsShellEnabled)) {
                assignToGlobalImports("ecm-customers", getDefaultReturnUrl());
            } else {
                navigate("/import-customers");
            }
        },
        [isGlobalImportsShellEnabled, navigate]
    );

    const noCertificateData = useSelector(selectNoCertificateData);
    const noCustomerData = useSelector(selectNoCustomerData);
    const isLoadingCustomerData = useSelector(selectCustomerLoading);
    const noBulkRequestData = useSelector(selectNoBulkRequestData);
    const noSingleRequestData = useSelector(SelectNoSingleRequestData);
    const noSavedSearchData = useSelector(selectNoSavedSearchData);
    const isReviewComplete = useSelector(selectIsReviewComplete);
    const bucketNoData = useSelector(selectBucketNoData);
    const noJobData = useSelector(selectNoJobData);
    let noDataMsg = null;

    if (props.certificate && noCertificateData) {
        switch (noCertificateData) {
            case "onLanding":
                noDataMsg = (
                    <React.Fragment>
                        <div id="no-data-landing-header">
                            You haven&apos;t added any certificates yet
                        </div>
                        <div id="helper-message">
                            To add your first certificate,
                            <Link to="/search/customers">search for a customer</Link>
                            then click &quot;Add a certificate&quot;. Or, you can
                            <Link to="/import-customers" onClick={goToCustomerImport}>
                                import all your customers and exemptions using our import template.
                            </Link>
                        </div>
                    </React.Fragment>
                );
                break;
            case "onSearch":
                noDataMsg = isReviewComplete ? (
                    <>
                        <div className="review-container">
                            <img
                                id="review-complete"
                                src={getAssetURL("ReviewComplete.svg")}
                                alt="review complete"
                                className="fit-image"
                            />
                            <div
                                id="review-certificate-done"
                                className="font-semibold font-normal text-lg">
                                You&apos;re all set for now.
                            </div>
                            <div id="filter-message">
                                All incoming certificates for this company have been reviewed. Good
                                work!
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="margin-top-sm" id="no-data-message">
                        <div
                            id="no-certificates-found"
                            className="font-semibold font-normal text-lg">
                            No matching exemption certificates found
                        </div>
                        <div id="filter-message" className="font-semibold font-normal text-sm">
                            Widen the date range or adjust your filters to find more exemption
                            certificates.
                        </div>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (noCustomerData && !isLoadingCustomerData) {
        switch (noCustomerData) {
            case "onLanding":
                noDataMsg = (
                    <React.Fragment>
                        <div id="no-data-landing-header">
                            You haven&apos;t added any customers yet
                        </div>
                        <div id="helper-message">
                            <Link to="/add-customer">Add your first customer</Link>
                            {}, or, you can {}
                            <Link to="/import-customers" onClick={goToCustomerImport}>
                                import all your customers and exemptions using our import template.
                            </Link>
                        </div>
                    </React.Fragment>
                );
                break;
            case "onSearch":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                {props.isVendor
                                    ? "No matching vendors found"
                                    : "No matching customers found"}
                            </div>
                        </div>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (props.request === REQUEST_TYPES.BULK_REQUESTS && noBulkRequestData) {
        switch (noBulkRequestData) {
            case "onLanding":
                noDataMsg = (
                    <React.Fragment>
                        <div id="no-data-message">You have no requests on file</div>

                        <div id="helper-message">
                            <Link to="/requests/create" target="_blank" rel="noreferrer">
                                Get started by creating a campaign
                            </Link>
                        </div>
                    </React.Fragment>
                );
                break;
            case "onSearch":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                No matching requests found
                            </div>
                        </div>
                    </div>
                );
                break;
            case "onCustomerSearch":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                No matching customers found
                            </div>
                        </div>
                    </div>
                );
                break;
            case "noResource":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                Resource not found
                            </div>
                        </div>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (props.request === REQUEST_TYPES.DELETED_REQUESTS && noBulkRequestData) {
        switch (noBulkRequestData) {
            case "onLanding":
                noDataMsg = (
                    <React.Fragment>
                        <div id="no-data-message">You have no deleted requests</div>
                    </React.Fragment>
                );
                break;
            case "onSearch":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                No matching deleted requests found
                            </div>
                        </div>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (props.request === REQUEST_TYPES.SINGLE_REQUESTS && noSingleRequestData) {
        switch (noSingleRequestData) {
            case "onLanding":
                noDataMsg = (
                    <React.Fragment>
                        <div id="no-data-message">You have no requests on file</div>

                        <div id="helper-message">
                            <Link to="/requests/create" target="_blank" rel="noreferrer">
                                Get started by creating a campaign
                            </Link>
                        </div>
                    </React.Fragment>
                );
                break;
            case "onSearch":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                No matching requests found
                            </div>
                        </div>
                    </div>
                );
                break;
            case "onCustomerSearch":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                No matching customers found
                            </div>
                        </div>
                    </div>
                );
                break;
            case "noResource":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-customers-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                Resource not found
                            </div>
                        </div>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (props.savedSearch && noSavedSearchData) {
        switch (noSavedSearchData) {
            case "onLanding":
                noDataMsg = <div id="no-data-landing-header">You have no saved searches</div>;
                break;
            case "onSearch":
                noDataMsg = (
                    <div className="margin-top-sm">
                        <div id="no-savedSearch-found" className="margin-top-sm">
                            <div className="font-semibold font-normal text-lg">
                                No matching saved searches found
                            </div>
                        </div>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (props.retailCustomer || props.retailCustomerForm) {
        noDataMsg = (
            <div className="margin-all-xl">
                <div
                    id="no-certificate-request-found"
                    className={
                        props.retailCustomerForm ? "color-gray-dark" : "text-center color-gray-dark"
                    }>
                    <div className="font-semibold font-normal text-lg">No customer found</div>
                    <div>Adjust your search or add a new customer.</div>
                </div>
            </div>
        );
    } else if (props.companyGroup) {
        noDataMsg = (
            <div className="text-center flex dl-flex-fill-height dl-flex-center">
                <h1 className="text-color-no-data">No Company Groups Found</h1>
                <h3 className="text-color-no-data">Adjust your search and try again</h3>
            </div>
        );
    } else if (props.bucket && bucketNoData) {
        switch (bucketNoData) {
            case "bucketGrid":
                noDataMsg = (
                    <div className="text-center flex dl-flex-fill-height dl-flex-center">
                        <h1 className="text-color-no-data">No Buckets Found</h1>
                    </div>
                );
                break;
            case "bucketLocations":
                noDataMsg = (
                    <div className="text-center flex dl-flex-fill-height dl-flex-center">
                        <h1 className="text-color-no-data">No Locations Assigned</h1>
                        <h3 className="text-color-no-data">Assign locations to this bucket</h3>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (props.job && noJobData) {
        switch (noJobData) {
            case "onLanding":
                noDataMsg = (
                    <React.Fragment>
                        <div id="no-data-landing-header">You don&apos;t have any jobs yet</div>
                        <div id="helper-message">You can create a job using the button above.</div>
                    </React.Fragment>
                );
                break;
            case "onSearch":
                noDataMsg = (
                    <div className="margin-top-sm" id="no-data-message">
                        <div className="font-semibold font-normal text-lg">
                            No matching jobs found
                        </div>
                        <div className="font-semibold font-normal text-sm">
                            Adjust your search and try again.
                        </div>
                    </div>
                );
                break;
            default:
                noDataMsg = null;
        }
    } else if (props.showGenNoDataMsg) {
        noDataMsg = (
            <div className="text-center flex dl-flex-fill-height no-data-centered">
                <h1 className="text-color-no-data margin-bottom-xs">{noDataMessage}</h1>
                <p className="text-color-no-data margin-all-none">{noDataDescription}</p>
            </div>
        );
    }
    return <div data-testid={testIDs.shared.noData}>{noDataMsg}</div>;
});

export default NoData;
